import { useSyncExternalStore } from 'react';
import type { User } from '@/lib/api/schemas';

/**
 * Tiny external auth store (no dependency). Source of truth for the bearer token and current user.
 *
 * status:
 *  - 'unknown'        token present in storage but /auth/me not yet confirmed (app bootstrap)
 *  - 'authenticated'  user loaded
 *  - 'anonymous'      no valid session
 */
export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

export interface AuthState {
  token: string | null;
  user: User | null;
  status: AuthStatus;
  /** Why we were signed out automatically ('expired' after a 401), shown once on the login page. */
  reason: 'expired' | null;
}

export const TOKEN_KEY = 'pos.token';

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}
function writeToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable: session lives in memory only */
  }
}

function initial(): AuthState {
  const token = readToken();
  return { token, user: null, status: token ? 'unknown' : 'anonymous', reason: null };
}

let state: AuthState = initial();
const listeners = new Set<() => void>();

function set(next: AuthState) {
  state = next;
  listeners.forEach((l) => l());
}

export const authStore = {
  getState: (): AuthState => state,
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** After a successful login. */
  setSession(token: string, user: User) {
    writeToken(token);
    set({ token, user, status: 'authenticated', reason: null });
  },
  /** After /auth/me or a profile change. */
  setUser(user: User) {
    set({ ...state, user, status: 'authenticated' });
  },
  /** 403 PASSWORD_CHANGE_REQUIRED: keep the session but force the change-password page. */
  markPasswordChangeRequired() {
    if (state.user && !state.user.must_change_password) {
      set({ ...state, user: { ...state.user, must_change_password: true } });
    }
  },
  /** Page-reload state: a token exists but the user has not been verified with /auth/me yet. */
  hydrate(token: string) {
    writeToken(token);
    set({ token, user: null, status: 'unknown', reason: null });
  },
  /** Sign out locally (logout button, 401). Does NOT call the API. */
  clear(reason: AuthState['reason'] = null) {
    writeToken(null);
    set({ token: null, user: null, status: 'anonymous', reason });
  },
  /** Test helper: reset to a pristine state. */
  reset() {
    writeToken(null);
    set({ token: null, user: null, status: 'anonymous', reason: null });
  },
};

export function useAuthState(): AuthState {
  return useSyncExternalStore(authStore.subscribe, authStore.getState, authStore.getState);
}
